import { useCallback, useEffect, useState, type TransitionEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HOUR_NAMES,
  TABOO_CASES,
  TABOO_SECTIONS,
  TABOO_SUMMARY,
  ZHANGJUE_LIST,
  buildDivinationPostDraft,
  calendarDivination,
  clearDivinationHistory,
  createPost,
  daliurenBrief,
  daliurenDivination,
  daliurenLunarText,
  deleteDivinationRecord,
  displayDay,
  displayHour,
  displayMonth,
  fetchDivinationHistory,
  formatDateTime,
  getCaptcha,
  historyQuestionLabel,
  historySystem,
  parseDaLiuRenResult,
  parseDivinationResult,
  randomStrokeText,
  saveDivination,
  strokeBreakdown,
  strokeDivination,
  strokeState,
  type CalendarSample,
  type DaLiuRenResult,
  type DivinationHistoryItem,
  type DivinationResult,
  type DivinationSystem,
  type Gender,
  type StrokeSample,
} from '@suangua/shared';
import { useAuth } from '../lib/auth/AuthContext';
import { useUI, themeLabel } from '../lib/ui/UIContext';
import { fortuneBadgeClass } from '../components/ui/atoms';
import { MarkdownView } from '../components/board/MarkdownView';
import { DaLiuRenPanel } from '../components/divination/DaLiuRenPanel';

/**
 * 占卜页（原型 divine 屏复刻）：
 * 左栏 = 取数模式 + 所问之事 + 六宫轮盘（金针旋转落宫）
 * 右栏 = 推演结果 / 空状态占位 + 占卜历史（登录用户，可展开/删除/清空）
 * 推演算法走 @suangua/shared 引擎（与原型完全一致）。
 */

/** 单轮待展示结果（金针停止后才呈现） */
interface PendingResult {
  result: DivinationResult;
  question: string;
  numsLabel: string;
}

export function DivinationPage() {
  const { user } = useAuth();
  const { theme, showToast } = useUI();

  // 占卜体系：小六壬（六神煞）/ 大六壬
  const [system, setSystem] = useState<DivinationSystem>('xiaoliuren');

  // 取数模式与输入（默认「随机取数」：日历取数在同一时辰内结果基本相同，参考性较弱）
  const [mode, setMode] = useState<'calendar' | 'stroke'>('stroke');
  const [question, setQuestion] = useState('');
  const [strokeInput, setStrokeInput] = useState('');
  const [strokeError, setStrokeError] = useState('');

  // 日历 / 笔画取样结果（含原始 month/day/hour/strokes，用于展示取数说明）
  const [calendarSample, setCalendarSample] = useState<CalendarSample | null>(null);
  const [strokeSample, setStrokeSample] = useState<StrokeSample | null>(null);

  // 大六壬盘面（时间起课）
  const [daResult, setDaResult] = useState<DaLiuRenResult | null>(null);
  const [daSavedTip, setDaSavedTip] = useState('');
  // 大六壬 本命 / 行年（选填，本地记忆）
  const [dlrPref] = useState(loadDlrPref);
  const [birthYear, setBirthYear] = useState(dlrPref.birthYear);
  const [gender, setGender] = useState<Gender>(dlrPref.gender);

  // 轮盘状态：金针角度 / 旋转中 / 待展示与已展示结果
  const [spinning, setSpinning] = useState(false);
  const [needleAngle, setNeedleAngle] = useState(0);
  const [pending, setPending] = useState<PendingResult | null>(null);
  const [shown, setShown] = useState<PendingResult | null>(null);

  // 历史（登录用户）
  const [history, setHistory] = useState<DivinationHistoryItem[]>([]);
  // 转入留言板（占卜发帖）目标记录
  const [shareItem, setShareItem] = useState<DivinationHistoryItem | null>(null);
  const [savedTip, setSavedTip] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  // 占卜禁忌弹窗
  const [tabooOpen, setTabooOpen] = useState(false);

  /** 当前时辰名（页头副标题） */
  const hourName = HOUR_NAMES[hourIndex() - 1] ?? '';
  /** 今日农历（页头副标题，不触发起卦） */
  const [todayLunar] = useState(() => calendarDivination().lunarText);

  /**
   * 切到「日历取数」且尚无样本时，自动取一次当前时辰样本并直接呈现（不播动画、不存档）。
   * 默认模式为随机取数，故进入页面不再自动起卦。
   */
  useEffect(() => {
    if (mode !== 'calendar' || calendarSample) return;
    const sample = calendarDivination();
    setCalendarSample(sample);
    setShown({
      result: sample.result,
      question: question.trim(),
      numsLabel: `农历${displayMonth(sample.month)}${displayDay(sample.day)} · ${displayHour(sample.hour)}`,
    });
    setNeedleAngle((sample.result.final.id - 1) * 60); // 金针直接指向落宫（无过渡）
  }, [mode, calendarSample, question]);

  /** 本命 / 行年偏好本地记忆（仅存本机，不落库） */
  useEffect(() => {
    saveDlrPref(birthYear, gender);
  }, [birthYear, gender]);

  /** 加载历史（登录后） */  const loadHistory = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetchDivinationHistory();
      setHistory(res.items);
    } catch {
      /* 静默：历史加载失败不影响主流程 */
    }
  }, [user]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  /** 保存到服务端历史（仅主动点击起卦时；登录才存） */
  const persistResult = useCallback(
    async (result: DivinationResult, sample: CalendarSample | StrokeSample, q: string) => {
      if (!user) {
        setSavedTip('登录后可保存到历史（当前为临时展示）');
        return;
      }
      const isCalendar = 'lunarText' in sample;
      try {
        await saveDivination({
          system: 'xiaoliuren',
          mode,
          question: q || null,
          lunar_text: isCalendar ? (sample as CalendarSample).lunarText : null,
          input: isCalendar
            ? { month: (sample as CalendarSample).month, day: (sample as CalendarSample).day, hour: (sample as CalendarSample).hour }
            : { text: (sample as StrokeSample).input, strokes: (sample as StrokeSample).strokes },
          result,
        });
        setSavedTip('已保存到占卜历史');
        await loadHistory();
      } catch {
        setSavedTip('保存失败，请稍后再试');
      }
    },
    [user, mode, loadHistory],
  );

  /** 切换取数模式（旋转中禁止；切换后同步结果区） */
  function selectMode(m: 'calendar' | 'stroke') {
    if (spinning) return;
    setMode(m);
    setStrokeError('');
    setSavedTip('');
    if (m === 'calendar' && calendarSample) {
      // 已有本时辰样本：直接回显，避免空白
      setShown({
        result: calendarSample.result,
        question: question.trim(),
        numsLabel: `农历${displayMonth(calendarSample.month)}${displayDay(calendarSample.day)} · ${displayHour(calendarSample.hour)}`,
      });
      setNeedleAngle((calendarSample.result.final.id - 1) * 60);
    } else {
      setShown(null);
    }
  }

  /** 随机抽字：填入取数输入框；「所问之事」为空时一并回填（使记录可追溯本次用字） */
  function drawRandomChars() {
    const text = randomStrokeText();
    setStrokeInput(text);
    setStrokeError('');
    if (!question.trim()) setQuestion(text);
    showToast(`随机抽字 · ${text}`);
  }

  /** 切换占卜体系（清空另一体系的盘面） */
  function selectSystem(s: DivinationSystem) {
    if (spinning || s === system) return;
    setSystem(s);
    setStrokeError('');
    setSavedTip('');
    setDaSavedTip('');
    if (s === 'daliuren') setShown(null);
    else setDaResult(null);
  }

  /** 大六壬起课：以当前时刻排盘（时间起课），登录用户即时存档 */
  async function runDaLiuRen() {
    const now = new Date();
    // 本命 / 行年：出生年份合法（≥1900 且不晚于今年）时才参与
    const yearNum = Number.parseInt(birthYear, 10);
    const opts =
      Number.isFinite(yearNum) && yearNum >= 1900 ? { birthYear: yearNum, gender } : {};
    const r = daliurenDivination(now, opts);
    setDaResult(r);
    setDaSavedTip('');
    showToast(opts.birthYear ? `起课完成 · ${r.keTi.name} · 行年${r.benMing?.xingNian ?? ''}` : `起课完成 · ${r.keTi.name}`);

    if (!user) {
      setDaSavedTip('登录后可保存到历史（当前为临时展示）');
      return;
    }
    try {
      await saveDivination({
        system: 'daliuren',
        mode: 'datetime',
        question: question.trim() || null,
        lunar_text: daliurenLunarText(r).slice(0, 64),
        input: { datetime: now.toISOString() },
        result: r,
      });
      setDaSavedTip('已保存到占卜历史');
      await loadHistory();
    } catch {
      setDaSavedTip('保存失败，请稍后再试');
    }
  }

  /** 点击起卦：取样 → 推演 → 存档 → 金针旋转落宫 */
  function runDivine() {
    if (spinning) return;

    let sample: CalendarSample | StrokeSample;
    // 本次记录的「所问之事」：随机抽字时会回填，故此处单独持有
    let ask = question.trim();

    if (mode === 'calendar') {
      sample = calendarDivination();
      setCalendarSample(sample);
    } else {
      let text = strokeInput.trim();
      if (!text) {
        // 未输入取数汉字 → 系统随机抽字；「所问之事」也为空时，把抽到的字回填为所问之事
        text = randomStrokeText();
        setStrokeInput(text);
        if (!ask) {
          ask = text;
          setQuestion(text);
        }
      }
      const s = strokeState(text);
      if (!s.ok) {
        setStrokeError(s.error.message);
        return;
      }
      setStrokeError('');
      sample = strokeDivination(text);
      setStrokeSample(sample);
    }

    const result = sample.result;
    const numsLabel =
      mode === 'calendar'
        ? `农历${displayMonth(sample.month)}${displayDay(sample.day)} · ${displayHour(sample.hour)}`
        : `取数 月${(sample as StrokeSample).month} · 日${(sample as StrokeSample).day} · 时${(sample as StrokeSample).hour}`;

    setPending({ result, question: ask, numsLabel });
    setSavedTip('');
    void persistResult(result, sample, ask);

    // 金针旋转：至少 3 圈后顺时针落到目标宫（与原型一致的缓动）
    setSpinning(true);
    setShown(null);
    const current = ((needleAngle % 360) + 360) % 360;
    const target = (result.final.id - 1) * 60;
    const delta = (target - current + 360) % 360;
    setNeedleAngle((a) => a + 1080 + delta);
  }

  /** 金针旋转结束（transitionend transform）→ 呈现结果 */
  function onNeedleStop(e: TransitionEvent<HTMLDivElement>) {
    if (e.target !== e.currentTarget || e.propertyName !== 'transform') return;
    if (!spinning || !pending) return;
    setSpinning(false);
    setShown(pending);
    showToast(`推演完成 · 落「${pending.result.final.name}」`);
    void loadHistory(); // 起卦时已保存，此处刷新列表取最新
  }

  const result = shown?.result ?? null;
  const lunarText = calendarSample?.lunarText ?? todayLunar;
  const isDa = system === 'daliuren';

  return (
    <section>
      {/* 页头 */}
      <div className="page-head">
        <h1 className="page-title font-zh">{isDa ? '大六壬 · 时间起课' : '六神煞 · 掌中诀'}</h1>
        {/* lunarText 已含时辰（如：农历二〇二六年八月初一未时），无需重复拼接 */}
        <span className="page-sub">{isDa ? (daResult?.siZhuText ?? '四柱 · 月将 · 天地盘 · 四课三传') : lunarText || hourName}</span>
        <span className="ml-auto flex items-center gap-2">
          <button className="chip cursor-pointer transition-colors hover:border-gold-500" onClick={() => setTabooOpen(true)}>
            <i className="fa-solid fa-book-open text-gold-500" /> 禁忌
          </button>
          <span className="chip">
            <i className="fa-solid fa-wand-sparkles text-gold-500" /> {themeLabel(theme)}
          </span>
        </span>
      </div>

      {/* 大六壬盘面信息量大：左栏收窄为控制栏，右栏留给盘面 */}
      <div
        className="divine-grid"
        style={isDa ? { gridTemplateColumns: 'minmax(0, 320px) minmax(0, 1fr)' } : undefined}
      >
        {/* ══ 左栏：体系 + 取数模式 + 六宫轮盘 / 大六壬起课 ══ */}
        <div className="space-y-5">
          <section className="ink-card p-5">
            <div className="seg">
              <button className={!isDa ? 'on' : ''} onClick={() => selectSystem('xiaoliuren')}>
                六神煞
              </button>
              <button className={isDa ? 'on' : ''} onClick={() => selectSystem('daliuren')}>
                大六壬
              </button>
            </div>

            <input
              className="field mt-4"
              value={question}
              maxLength={80}
              placeholder="所问之事（选填，≤80 字）"
              onChange={(e) => setQuestion(e.target.value)}
            />

            {isDa ? (
              <>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="shrink-0 text-[11px] text-t3">本命 / 行年</span>
                  <input
                    className="field max-w-[128px]"
                    value={birthYear}
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="出生年 1990"
                    onChange={(e) => setBirthYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  />
                  <div className="seg shrink-0">
                    <button className={gender === '男' ? 'on' : ''} onClick={() => setGender('男')}>
                      男
                    </button>
                    <button className={gender === '女' ? 'on' : ''} onClick={() => setGender('女')}>
                      女
                    </button>
                  </div>
                </div>

                <p className="mt-2 text-[11px] leading-relaxed text-t3">
                  填出生年后，除四柱月将外另推<em className="text-gold-500 not-italic">本命</em>与
                  <em className="text-gold-500 not-italic">行年</em>（男起丙寅顺行、女起壬申逆行），
                  行年上神纳入断课；仅存本机。
                </p>

                <p className="mt-3 text-[12px] leading-relaxed text-t2">
                  以占卜当下的<em className="text-gold-500 not-italic">四柱</em>与
                  <em className="text-gold-500 not-italic">月将</em>（中气换将）加占时起课，
                  依次布天地盘、四课、三传（九宗门）、十二天将，并出课体、断语与应期。
                </p>
              </>
            ) : (
              <>
                <div className="seg mt-4">
                  <button className={mode === 'stroke' ? 'on' : ''} onClick={() => selectMode('stroke')}>
                    随机取数
                  </button>
                  <button className={mode === 'calendar' ? 'on' : ''} onClick={() => selectMode('calendar')}>
                    日历取数
                  </button>
                </div>

                {mode === 'stroke' ? (
                  <>
                    <input
                      className="field mt-3"
                      value={strokeInput}
                      maxLength={10}
                      placeholder="输入 1~10 个汉字，凭第一印象（留空则随机抽字）"
                      onChange={(e) => setStrokeInput(e.target.value)}
                    />
                    <div className="mt-1.5 flex items-start justify-between gap-3">
                      {strokeError ? (
                        <span className="text-[11px] text-cinnabar-500">{strokeError}</span>
                      ) : strokeSample?.input === strokeInput.trim() && strokeInput.trim() ? (
                        <span className="text-[11px] text-t2">
                          笔画明细：{strokeBreakdown(strokeSample.input, strokeSample.strokes)}（合计{' '}
                          {strokeSample.strokes.reduce((a, b) => a + b, 0)}）
                        </span>
                      ) : (
                        <span className="text-[11px] text-t3">留空点「起卦」即由系统随机抽字取数</span>
                      )}
                      <button
                        className="shrink-0 text-[11px] text-t3 transition-colors hover:text-gold-500"
                        onClick={drawRandomChars}
                      >
                        <i className="fa-solid fa-dice text-gold-500" /> 随机抽字
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="mt-3 text-[12px] leading-relaxed text-t2">
                    以农历为准：从<em className="text-gold-500 not-italic">大安</em>
                    上起月，月上起日，日上起时，时上查掌诀。（同一时辰内结果相同，参考性较弱）
                  </p>
                )}
              </>
            )}
          </section>

          {isDa ? (
            /* 大六壬起课（时间起课，无随机输入） */
            <section className="flex flex-col items-center py-3">
              <div className="relative h-[94px] w-[94px]">
                <button className="wheel-core" onClick={() => void runDaLiuRen()}>
                  <i className="fa-solid fa-compass" />
                  <span>起课</span>
                </button>
              </div>
              <p className="mt-3 text-[11px] text-t3">点击「起课」— 以当前时刻排盘</p>
            </section>
          ) : (
            /* 六宫轮盘 */
            <section className="flex flex-col items-center py-3">
              <div className={`wheel${spinning ? ' is-spinning' : ''}`}>
                <div className="wheel-ring" />
                <div className="wheel-ring inner" />
                <div
                  className="needle"
                  style={{ transform: `rotate(${needleAngle}deg)` }}
                  onTransitionEnd={onNeedleStop}
                />

                {ZHANGJUE_LIST.map((p, i) => (
                  <div
                    key={p.id}
                    className="palace"
                    style={{ transform: `rotate(${i * 60}deg) translateY(-114px) rotate(${-i * 60}deg)` }}
                  >
                    <div className={`palace-btn${result && shown && result.final.id === p.id ? ' on' : ''}`}>
                      <span className="palace-dot" />
                      <span className="palace-name font-zh">{p.name}</span>
                    </div>
                  </div>
                ))}

                <button className="wheel-core" disabled={spinning} onClick={runDivine}>
                  <i className="fa-solid fa-wand-magic-sparkles" />
                  <span>{spinning ? '推演中' : '起卦'}</span>
                </button>
              </div>
              <p className="mt-3 text-[11px] text-t3">点击中央「起卦」— 金针旋转落宫</p>
            </section>
          )}
        </div>

        {/* ══ 右栏：推演结果（六神煞 / 大六壬盘面）+ 占卜历史 ══ */}
        <div className="space-y-5">
          {isDa ? (
            daResult ? (
              <>
                <div className="flex flex-wrap items-center gap-2 text-[10px] text-t2">
                  {question.trim() && <span className="chip-xs">「{question.trim()}」</span>}
                  <span className="chip-xs">{daResult.siZhuText}</span>
                  <span className="chip-xs">{daliurenBrief(daResult)}</span>
                  <span className="ml-auto text-t3">{daSavedTip || '仅供娱乐参考'}</span>
                </div>
                <DaLiuRenPanel result={daResult} />
              </>
            ) : (
              <section className="empty-card">
                <i className="fa-regular fa-compass" />
                <p>点击「起课」，以当下四柱月将排大六壬盘</p>
                <p className="text-[11px]">月将加时 · 天地盘 · 四课三传 · 十二天将 · 断课</p>
              </section>
            )
          ) : result && shown ? (
            <section className="ink-card result-in p-5" key={shown.numsLabel + result.final.id}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] tracking-[.25em] text-t2">最 终 落 宫</span>
                <span className={`fortune-badge ${fortuneBadgeClass(result.final.fortune)}`}>
                  {result.final.fortune}
                </span>
              </div>
              <p className="gold-text font-zh mt-2 text-4xl font-bold leading-none">{result.final.name}</p>
              <p className="mt-1.5 text-[13px] text-gold-400/90">{result.final.summary}</p>
              <p className="mt-2 text-[13px] leading-relaxed text-t2">{result.final.desc}</p>

              {/* 三步推演过程 */}
              <div className="mt-4 grid grid-cols-3 gap-2">
                {result.steps.map((s) => (
                  <div key={s.step} className="rounded-lg border border-line bg-fill px-2 py-2 text-center">
                    <p className="text-[10px] text-t3">{s.step}</p>
                    <p className="mt-1 whitespace-nowrap text-[11px] text-t1">
                      {s.fromName} <i className="fa-solid fa-arrow-right-long text-[9px] text-gold-500" /> {s.toName}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-3 flex items-center gap-2 text-[10px] text-t2">
                {shown.question && <span className="chip-xs">「{shown.question}」</span>}
                <span className="chip-xs">{shown.numsLabel}</span>
                <span className="ml-auto text-t3">{savedTip || '仅供娱乐参考'}</span>
              </div>
            </section>
          ) : (
            <section className="empty-card">
              <i className="fa-regular fa-scroll" />
              <p>起卦后，此处展示推演结果与卦象详解</p>
              <p className="text-[11px]">
                {mode === 'stroke' ? '随机抽字 · 笔画取数 · 三段成数' : '大安起月 · 月上起日 · 日上起时 · 时上落宫'}
              </p>
            </section>
          )}

          {/* 占卜历史 */}
          <HistoryPanel
            history={history}
            expandedId={expandedId}
            loggedIn={!!user}
            onToggle={(id) => setExpandedId((cur) => (cur === id ? null : id))}
            onDelete={async (id) => {
              await deleteDivinationRecord(id);
              await loadHistory();
            }}
            onClear={async () => {
              await clearDivinationHistory();
              await loadHistory();
              showToast('已清空占卜历史');
            }}
            onShare={(item) => setShareItem(item)}
          />
        </div>
      </div>

      {/* 转入留言板（占卜发帖）弹层 */}
      {shareItem && <ShareToBoardModal item={shareItem} onClose={() => setShareItem(null)} />}

      {/* 占卜禁忌弹窗 */}
      {tabooOpen && <TabooModal onClose={() => setTabooOpen(false)} />}
    </section>
  );
}

/**
 * 占卜禁忌弹窗：六类禁忌 + 可占/不可占示例 + 总结。
 * 内容来自 shared TABOO_*（与 h5 弹层共用同一数据源）。
 */
function TabooModal(props: { onClose: () => void }) {
  const { onClose } = props;

  return (
    /* 实色弹层结构（modal-panel 使用 --modal-bg 实底） */
    <div className="modal-wrap">
      <div className="modal-mask" onClick={onClose} />
      <div className="modal-panel !w-[min(640px,100%)]">
        <div className="modal-head">
          <p className="modal-title font-zh">占卜禁忌</p>
          <button className="icon-btn" onClick={onClose} aria-label="关闭">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="mt-3 max-h-[60vh] space-y-4 overflow-y-auto pr-1">
          {/* 六类禁忌 */}
          {TABOO_SECTIONS.map((section, si) => (
            <section key={section.name}>
              <p className="font-zh text-[13px] font-semibold text-gold-400">
                {['一', '二', '三', '四', '五', '六'][si]}、{section.name}
              </p>
              <ul className="mt-1.5 space-y-2">
                {section.items.map((item) => (
                  <li key={item.title} className="rounded-xl border border-line bg-fill px-3 py-2">
                    <p className="text-[12px] font-medium text-t1">· {item.title}</p>
                    <p className="mt-0.5 text-[11px] leading-relaxed text-t2">{item.desc}</p>
                  </li>
                ))}
              </ul>
            </section>
          ))}

          {/* 可占与不可占示例 */}
          <section>
            <p className="font-zh text-[13px] font-semibold text-gold-400">附：可占与不可占示例</p>
            <ul className="mt-1.5 space-y-1.5">
              {TABOO_CASES.map((c) => (
                <li key={c.topic} className="flex items-center gap-2 rounded-xl border border-line bg-fill px-3 py-1.5 text-[11px]">
                  <span className="w-24 shrink-0 truncate text-t1">{c.topic}</span>
                  <span
                    className={`shrink-0 rounded-md px-1.5 py-0.5 text-[10px] ${
                      c.allow === 'yes'
                        ? 'bg-jade-400/15 text-jade-400'
                        : c.allow === 'never'
                          ? 'bg-cinnabar-500/15 text-cinnabar-500'
                          : 'bg-gold-500/15 text-gold-400'
                    }`}
                  >
                    {c.allow === 'yes' ? '✓ 可占' : c.allow === 'never' ? '✗ 绝对禁止' : '✗ 不可占'}
                  </span>
                  <span className="min-w-0 flex-1 text-right text-t2">{c.reason}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* 总结 */}
          <p className="rounded-xl border border-gold-500/30 bg-gold-500/5 px-3 py-2 text-[11px] leading-relaxed text-t2">
            <span className="font-medium text-gold-400">总结</span>：{TABOO_SUMMARY}
          </p>
        </div>

        <div className="mt-4 flex justify-end">
          <button className="gold-btn" onClick={onClose}>
            我记住了
          </button>
        </div>
      </div>
    </div>
  );
}

/** 真实时辰序号 1..12（子 = 23-1 点，每 2 小时一辰） */
function hourIndex(): number {
  return Math.floor(((new Date().getHours() + 1) % 24) / 2) + 1;
}

/** 吉凶评级 → 历史列表徽章样式（含大六壬的 平 / 小凶） */
function fortuneBadgeTone(fortune: string | undefined): string {
  switch (fortune) {
    case '吉':
      return 'border-jade-400/40 bg-jade-400/10 text-jade-400';
    case '中吉':
      return 'border-gold-500/40 bg-gold-500/10 text-gold-400';
    case '小凶':
      return 'border-cinnabar-500/30 bg-cinnabar-500/10 text-cinnabar-500';
    case '凶':
      return 'border-cinnabar-500/50 bg-cinnabar-500/15 text-cinnabar-500';
    default:
      return 'border-line bg-fill text-t2';
  }
}

/** 大六壬「本命 / 行年」本地记忆（选填，仅存本机） */
const DLR_PREF_KEY = 'suangua.dlr.benming';

function loadDlrPref(): { birthYear: string; gender: Gender } {
  try {
    const raw = localStorage.getItem(DLR_PREF_KEY);
    if (raw) {
      const p = JSON.parse(raw) as { birthYear?: unknown; gender?: unknown };
      const year = typeof p.birthYear === 'string' ? p.birthYear.replace(/\D/g, '').slice(0, 4) : '';
      return { birthYear: year, gender: p.gender === '女' ? '女' : '男' };
    }
  } catch {
    /* 隐私模式 / 存储不可用：静默降级 */
  }
  return { birthYear: '', gender: '男' };
}

function saveDlrPref(birthYear: string, gender: Gender): void {
  try {
    localStorage.setItem(DLR_PREF_KEY, JSON.stringify({ birthYear, gender }));
  } catch {
    /* 静默 */
  }
}

/** 占卜历史（登录展示；行点击展开推演详情，可转帖/删除/清空） */
function HistoryPanel(props: {
  history: DivinationHistoryItem[];
  expandedId: number | null;
  loggedIn: boolean;
  onToggle: (id: number) => void;
  onDelete: (id: number) => Promise<void>;
  onClear: () => Promise<void>;
  /** 转入留言板（占卜发帖） */
  onShare: (item: DivinationHistoryItem) => void;
}) {
  const { history, expandedId, loggedIn, onToggle, onDelete, onClear, onShare } = props;

  if (!loggedIn) {
    return (
      <section>
        <h2 className="section-title">占 卜 历 史</h2>
        <div className="empty-card !py-8">
          <i className="fa-regular fa-clock" />
          <p>登录后可保存与查看最近 100 条占卜历史</p>
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="flex items-baseline justify-between">
        <h2 className="section-title">占 卜 历 史</h2>
        {history.length > 0 && (
          <button
            className="text-[11px] text-t3 transition-colors hover:text-cinnabar-500"
            onClick={() => void onClear()}
          >
            清空全部
          </button>
        )}
      </div>
      <div className="ink-card divide-y divide-line">
        {history.length === 0 ? (
          <p className="py-8 text-center text-[13px] text-t3">暂无占卜历史，起一卦试试</p>
        ) : (
          history.map((item) => {
            // 按体系分派解析（旧数据无 system → 六神煞，解析失败降级为基础展示）
            const isDa = historySystem(item) === 'daliuren';
            const parsed = isDa ? null : parseDivinationResult(item);
            const da = isDa ? parseDaLiuRenResult(item) : null;
            const fortune = isDa ? da?.interpretation.fortune : parsed?.final.fortune;
            const expanded = expandedId === item.id;
            const modeText =
              item.mode === 'calendar' ? '日历取数' : item.mode === 'stroke' ? '随机取数' : '时间起课';
            return (
              <div key={item.id} className="flex items-center gap-3 px-5 py-3">
                <span
                  className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] ${fortuneBadgeTone(fortune)}`}
                >
                  {fortune ?? '—'}
                </span>
                <button className="min-w-0 flex-1 text-left" onClick={() => (parsed || da) && onToggle(item.id)}>
                  <p className="truncate text-[13px] text-t1">
                    <span className="font-zh font-semibold text-gold-400">
                      {isDa ? (da?.keTi.name ?? '大六壬') : (parsed?.final.name ?? '卦')}
                    </span>
                    <span className="text-t2">
                      {' '}
                      · {historyQuestionLabel(item)}
                    </span>
                  </p>
                  <p className="mt-0.5 text-[10px] text-t3">
                    {isDa ? '大六壬 · 时间起课' : `六神煞 · ${modeText}`}
                    {isDa && da && ` · 三传${da.chuan.map((c) => c.zhi).join('')}`}
                    {!isDa && parsed && ` · ${parsed.final.fortune}`}
                    {expanded && parsed && (
                      <span className="mt-1 block leading-relaxed">
                        {parsed.steps.map((s) => s.desc).join(' ')}
                        最终：{parsed.final.name}（{parsed.final.fortune}）— {parsed.final.summary}
                      </span>
                    )}
                    {expanded && da && (
                      <span className="mt-1 block leading-relaxed">
                        {da.siZhuText}　月将{da.yueJiang.zhi}
                        {da.yueJiang.name}　占时{da.zhanShi}　旬空{da.kongWang.join('')}
                        <br />
                        四课：{da.ke.map((k) => `${k.xia}/${k.shang}`).join('　')}
                        <br />
                        三传：
                        {da.chuan
                          .map((c) => `${c.position}${c.zhi}(${c.tianJiang}·${c.liuQin}${c.kong ? '·空' : ''})`)
                          .join('　')}
                        <br />
                        {da.interpretation.summary} —{' '}
                        {da.interpretation.points
                          .slice(0, 3)
                          .map((p) => p.text.split(' — ')[0])
                          .join('；')}
                      </span>
                    )}
                  </p>
                </button>
                <span className="shrink-0 text-[10px] text-t3">{formatDateTime(item.created_at)}</span>
                <button
                  className="shrink-0 text-[10px] text-t3 hover:text-gold-500"
                  onClick={() => onShare(item)}
                  title="转入留言板"
                >
                  转帖
                </button>
                <button
                  className="shrink-0 text-[10px] text-t3 hover:text-cinnabar-500"
                  onClick={() => void onDelete(item.id)}
                >
                  删除
                </button>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

/** 灵验标记选项（key 空 = 未定） */
const VERDICT_OPTIONS: Array<[string, string]> = [
  ['', '未定'],
  ['accurate', '灵'],
  ['inaccurate', '不灵'],
  ['uncertain', '不好说'],
];

/**
 * 转入留言板弹层：占卜历史 → 占卜帖（board=divination）
 * 标题预填（所问之事未填时自动生成简约标题，可改）+ 灵验标记 + 验证码，发布后跳转帖子详情。
 */
function ShareToBoardModal(props: { item: DivinationHistoryItem; onClose: () => void }) {
  const { item, onClose } = props;
  const navigate = useNavigate();
  const { showToast } = useUI();
  const draft = buildDivinationPostDraft(item);

  const [title, setTitle] = useState(draft.title);
  const [verdict, setVerdict] = useState('');
  const [captchaSvg, setCaptchaSvg] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadCaptcha = useCallback(async () => {
    try {
      const res = await getCaptcha();
      setCaptchaId(res.captcha_id);
      setCaptchaSvg(res.svg);
      setCaptchaAnswer('');
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    void loadCaptcha();
  }, [loadCaptcha]);

  async function submit() {
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await createPost({
        board: 'divination',
        title: title.trim() || null,
        content: draft.content,
        verdict: verdict || null,
        captcha_id: captchaId,
        captcha_answer: Number(captchaAnswer),
      });
      onClose();
      showToast('占卜帖已发布');
      navigate(`/board/${res.id}`);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : '发布失败');
      void loadCaptcha();
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = !!captchaId && captchaAnswer.length > 0 && !submitting;

  return (
    /* 实色弹层结构（modal-panel 使用 --modal-bg 实底；ink-card 为半透明卡片，浮层上会透出背景） */
    <div className="modal-wrap">
      <div className="modal-mask" onClick={onClose} />
      <div className="modal-panel !w-[min(560px,100%)]">
        <div className="modal-head">
          <p className="modal-title font-zh">转入留言板</p>
          <button className="icon-btn" onClick={onClose} aria-label="关闭">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <p className="mt-1 text-[11px] text-t3">
          所问之事未填时自动生成简约标题；正文由推演过程与掌诀结果汇总生成。
        </p>

        {/* 标题（预填，可编辑） */}
        <input
          className="mt-3 w-full rounded-xl border border-line bg-fill px-3 py-2.5 text-[13px] text-t1 outline-none focus:border-gold-500"
          placeholder="标题"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={80}
        />

        {/* 正文预览 */}
        <div className="mt-3 max-h-52 overflow-y-auto rounded-xl border border-line bg-fill px-3 py-2.5">
          <MarkdownView content={draft.content} className="text-[12px]" />
        </div>

        {/* 灵验标记 */}
        <p className="mt-3 text-[11px] text-t3">灵验标记</p>
        <div className="mt-1.5 flex gap-2">
          {VERDICT_OPTIONS.map(([key, label]) => (
            <button
              key={key || 'none'}
              onClick={() => setVerdict(key)}
              className={`rounded-lg border px-3 py-1.5 text-[12px] transition-colors ${
                verdict === key ? 'border-gold-500 bg-gold-500/10 text-gold-400' : 'border-line text-t2 hover:border-t3'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* 验证码 */}
        <div className="mt-3 flex items-center gap-2">
          {captchaSvg && (
            <>
              <div dangerouslySetInnerHTML={{ __html: captchaSvg }} className="h-10 shrink-0" />
              <input
                className="w-24 rounded-xl border border-line bg-fill px-3 py-2 text-[13px] text-t1 outline-none focus:border-gold-500"
                inputMode="numeric"
                placeholder="算式答案"
                value={captchaAnswer}
                onChange={(e) => setCaptchaAnswer(e.target.value)}
              />
              <button className="text-[11px] text-t3 hover:text-t1" onClick={() => void loadCaptcha()}>
                换一个
              </button>
            </>
          )}
        </div>
        {error && <p className="mt-2 text-[11px] text-cinnabar-500">{error}</p>}

        <div className="mt-4 flex justify-end gap-2">
          <button className="ghost-btn" onClick={onClose}>
            取消
          </button>
          <button className="gold-btn" disabled={!canSubmit} onClick={() => void submit()}>
            {submitting ? '发布中…' : '发布占卜帖'}
          </button>
        </div>
      </div>
    </div>
  );
}
